import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Grid, TextField, MenuItem,
  Alert, CircularProgress, Stack,
} from '@mui/material';
import { ArrowBack, Agriculture, AutoAwesome } from '@mui/icons-material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { farmOperationService, projectService, aiAdvisorService, seasonService } from '@smartgrow/shared';
import { CurrencyInput } from '../../components/CurrencyInput';
import type { CreateFarmOperationRequest, OperationType, FarmSeason, YieldUnit, PriceSuggestionResponse, YieldEstimateResponse } from '@smartgrow/shared';

const YIELD_UNITS: { value: YieldUnit; label: string }[] = [
  { value: 'KG', label: 'Kilograms (kg)' },
  { value: 'BAG_50KG', label: 'Bags (50kg)' },
  { value: 'BAG_100KG', label: 'Bags (100kg)' },
  { value: 'TON', label: 'Tonnes' },
  { value: 'UNIT', label: 'Units (piece count)' },
];

const OPERATION_TYPES: { value: OperationType; label: string; seasonal: boolean }[] = [
  { value: 'SEASONAL_CROP', label: 'Seasonal Crop (corn, cassava, rice, yam)', seasonal: true },
  { value: 'PERENNIAL_CROP', label: 'Perennial Crop (cashew, mango, oil palm, cocoa)', seasonal: false },
  { value: 'LIVESTOCK', label: 'Livestock (fish, cattle, poultry, goat)', seasonal: false },
  { value: 'GENERAL', label: 'General Farm Operation', seasonal: false },
];

const SEASONS: { value: FarmSeason; label: string }[] = [
  { value: 'RAINY', label: 'Rainy Season' },
  { value: 'DRY', label: 'Dry Season' },
];

export const NewFarmOperationPage = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');

  const [form, setForm] = useState<CreateFarmOperationRequest>({
    projectId: '',
    name: '',
    operationType: 'SEASONAL_CROP',
    season: 'RAINY',
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects-all'],
    queryFn: () => projectService.getProjects({ size: 100 }),
  });

  const { data: seasons = [] } = useQuery({
    queryKey: ['seasons'],
    queryFn: () => seasonService.getAll(),
  });

  const projects = projectsData?.content ?? [];

  const selectedType = OPERATION_TYPES.find((t) => t.value === form.operationType);
  const isCrop = form.operationType === 'SEASONAL_CROP' || form.operationType === 'PERENNIAL_CROP';
  const isLivestock = form.operationType === 'LIVESTOCK';
  const isSeasonal = selectedType?.seasonal ?? false;

  const set = (field: keyof CreateFarmOperationRequest, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setYieldProjection = (field: 'expectedYieldQuantity' | 'expectedPricePerUnit', value: number | undefined) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      const quantity = field === 'expectedYieldQuantity' ? value : prev.expectedYieldQuantity;
      const price = field === 'expectedPricePerUnit' ? value : prev.expectedPricePerUnit;
      next.expectedRevenue = quantity && price ? quantity * price : undefined;
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: farmOperationService.create,
    onSuccess: (op) => navigate(`/dashboard/farm/${op.id}`),
    onError: () => setError('Failed to create farm operation. Check all required fields.'),
  });

  const [priceSuggestion, setPriceSuggestion] = useState<PriceSuggestionResponse | null>(null);
  const [priceSuggestionError, setPriceSuggestionError] = useState('');
  const suggestPriceMutation = useMutation({
    mutationFn: () => aiAdvisorService.suggestPrice({
      cropVariety: form.cropVariety || '',
      unitLabel: YIELD_UNITS.find((u) => u.value === form.yieldUnit)?.label,
    }),
    onSuccess: (res) => { setPriceSuggestion(res); setPriceSuggestionError(''); },
    onError: () => { setPriceSuggestion(null); setPriceSuggestionError('Failed to get a price suggestion. Try again.'); },
  });

  const applyPriceSuggestion = () => {
    if (priceSuggestion?.pricePerUnit != null) {
      setYieldProjection('expectedPricePerUnit', priceSuggestion.pricePerUnit);
    }
  };

  const [yieldEstimate, setYieldEstimate] = useState<YieldEstimateResponse | null>(null);
  const [yieldEstimateError, setYieldEstimateError] = useState('');
  const selectedProject = projects.find((p) => p.id === form.projectId);
  const estimateYieldMutation = useMutation({
    mutationFn: () => aiAdvisorService.estimateYieldForNewOperation({
      cropVariety: form.cropVariety || form.name || '',
      operationType: form.operationType,
      landAreaAcres: form.landAreaAcres,
      location: selectedProject?.location,
      season: form.season,
      preferredYieldUnit: form.yieldUnit,
    }),
    onSuccess: (res) => { setYieldEstimate(res); setYieldEstimateError(''); },
    onError: () => { setYieldEstimate(null); setYieldEstimateError('Failed to get a yield estimate. Try again.'); },
  });

  const applyYieldEstimate = () => {
    if (!yieldEstimate) return;
    setForm((prev) => {
      const next = { ...prev };
      if (yieldEstimate.yieldQuantity != null) next.expectedYieldQuantity = yieldEstimate.yieldQuantity;
      if (yieldEstimate.yieldUnit != null) next.yieldUnit = yieldEstimate.yieldUnit;
      if (yieldEstimate.pricePerUnit != null) next.expectedPricePerUnit = yieldEstimate.pricePerUnit;
      next.expectedHarvestingCost = yieldEstimate.harvestingCost;
      next.expectedBaggingCost = yieldEstimate.baggingCost;
      next.expectedTransportationCost = yieldEstimate.transportationCost;
      next.expectedFinalCost = yieldEstimate.finalCost;
      const quantity = next.expectedYieldQuantity;
      const price = next.expectedPricePerUnit;
      next.expectedRevenue = quantity && price ? quantity * price : undefined;
      next.expectedNetRevenue = next.expectedRevenue != null && next.expectedFinalCost != null
        ? next.expectedRevenue - next.expectedFinalCost
        : undefined;
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.projectId) { setError('Please select a project.'); return; }
    if (!form.name?.trim()) { setError('Operation name is required.'); return; }
    setError('');
    const payload: CreateFarmOperationRequest = { ...form };
    if (!isSeasonal) delete payload.season;
    if (!isCrop) {
      delete payload.cropVariety;
      delete payload.landAreaAcres;
      delete payload.expectedYieldKg;
      delete payload.yieldUnit;
      delete payload.expectedYieldQuantity;
      delete payload.expectedPricePerUnit;
      delete payload.expectedHarvestingCost;
      delete payload.expectedBaggingCost;
      delete payload.expectedTransportationCost;
      delete payload.expectedFinalCost;
      delete payload.expectedNetRevenue;
    }
    if (!isLivestock) { delete payload.animalType; delete payload.animalQuantity; }
    mutation.mutate(payload);
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="text">
          Back
        </Button>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Agriculture color="primary" />
          <Typography variant="h4" fontWeight="bold">New Farm Operation</Typography>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Operation Details</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth required label="Linked Project"
                value={form.projectId || ''}
                onChange={(e) => set('projectId', e.target.value)}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.projectName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required label="Operation Name"
                placeholder="e.g. 2025 Rainy Season Corn, Fish Pond Batch 1"
                value={form.name ?? ''}
                onChange={(e) => set('name', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select fullWidth required label="Operation Type"
                value={form.operationType}
                onChange={(e) => {
                  const t = e.target.value as OperationType;
                  setForm((prev) => ({ ...prev, operationType: t, season: undefined }));
                }}
              >
                {OPERATION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </TextField>
            </Grid>

            {isSeasonal && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    select fullWidth label="Season Type" value={form.season ?? ''}
                    onChange={(e) => set('season', e.target.value as FarmSeason)}
                  >
                    {SEASONS.map((s) => (
                      <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select fullWidth label="Season (year-specific, optional)" value={form.seasonInstanceId ?? ''}
                    onChange={(e) => set('seasonInstanceId', e.target.value || undefined)}
                    helperText="Enables season-over-season comparison, e.g. 2026 vs 2027"
                  >
                    <MenuItem value="">— None —</MenuItem>
                    {seasons.map((s) => (
                      <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <CurrencyInput
                fullWidth label="Operation Budget"
                value={form.operationBudget ?? null}
                onChange={(v) => set('operationBudget', v)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth multiline rows={3} label="Notes"
                value={form.notes ?? ''}
                onChange={(e) => set('notes', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>

        {(isCrop) && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Crop Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Crop Variety"
                  placeholder="e.g. Yellow Corn, White Cassava"
                  value={form.cropVariety ?? ''}
                  onChange={(e) => set('cropVariety', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" label="Land Area (acres)"
                  value={form.landAreaAcres ?? ''}
                  onChange={(e) => set('landAreaAcres', Number.parseFloat(e.target.value) || undefined)}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth type="number" label="Expected Yield Quantity"
                  placeholder="e.g. 55"
                  value={form.expectedYieldQuantity ?? ''}
                  onChange={(e) => setYieldProjection('expectedYieldQuantity', Number.parseFloat(e.target.value) || undefined)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  select fullWidth label="Yield Unit"
                  value={form.yieldUnit ?? ''}
                  onChange={(e) => set('yieldUnit', e.target.value as YieldUnit)}
                >
                  {YIELD_UNITS.map((u) => (
                    <MenuItem key={u.value} value={u.value}>{u.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={4}>
                <CurrencyInput
                  fullWidth label="Expected Price per Unit"
                  value={form.expectedPricePerUnit ?? null}
                  onChange={(v) => setYieldProjection('expectedPricePerUnit', v ?? undefined)}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack direction="row" spacing={1.5} flexWrap="wrap">
                  <Button
                    size="small" variant="outlined" startIcon={suggestPriceMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
                    disabled={!form.cropVariety || suggestPriceMutation.isPending}
                    onClick={() => { setPriceSuggestion(null); setPriceSuggestionError(''); suggestPriceMutation.mutate(); }}
                  >
                    Suggest price (AI estimate)
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={estimateYieldMutation.isPending ? <CircularProgress size={14} /> : <AutoAwesome />}
                    disabled={(!form.cropVariety && !form.name) || estimateYieldMutation.isPending}
                    onClick={() => { setYieldEstimate(null); setYieldEstimateError(''); estimateYieldMutation.mutate(); }}
                  >
                    Estimate yield &amp; revenue (AI estimate)
                  </Button>
                </Stack>
                {priceSuggestionError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setPriceSuggestionError('')}>{priceSuggestionError}</Alert>
                )}
                {priceSuggestion && (
                  <Alert
                    severity={priceSuggestion.error ? 'warning' : 'info'} sx={{ mt: 1.5 }} onClose={() => setPriceSuggestion(null)}
                    action={!priceSuggestion.error && priceSuggestion.pricePerUnit != null && (
                      <Button color="inherit" size="small" onClick={applyPriceSuggestion}>Apply</Button>
                    )}
                  >
                    {priceSuggestion.reasoning}
                  </Alert>
                )}
                {yieldEstimateError && (
                  <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setYieldEstimateError('')}>{yieldEstimateError}</Alert>
                )}
                {yieldEstimate && (
                  <Alert
                    severity={yieldEstimate.error ? 'warning' : 'info'} sx={{ mt: 1.5 }} onClose={() => setYieldEstimate(null)}
                    action={!yieldEstimate.error && (yieldEstimate.yieldQuantity != null || yieldEstimate.pricePerUnit != null) && (
                      <Button color="inherit" size="small" onClick={applyYieldEstimate}>Apply</Button>
                    )}
                  >
                    {yieldEstimate.reasoning}
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth disabled label="Estimated Gross Revenue (₦, auto-computed)"
                  value={form.expectedRevenue ? form.expectedRevenue.toLocaleString('en-NG') : ''}
                  helperText="Quantity × Price per unit"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth disabled label="Estimated Final Cost (₦)"
                  value={form.expectedFinalCost != null ? form.expectedFinalCost.toLocaleString('en-NG') : ''}
                  helperText="Cultivation + harvesting + bagging + transport (AI estimate)" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth disabled label="Estimated Net Revenue (₦)"
                  value={form.expectedNetRevenue != null ? form.expectedNetRevenue.toLocaleString('en-NG') : ''}
                  helperText="Gross revenue − final cost" />
              </Grid>
              {(form.expectedHarvestingCost != null || form.expectedBaggingCost != null || form.expectedTransportationCost != null) && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Cost breakdown — Harvesting: {form.expectedHarvestingCost != null ? `₦${form.expectedHarvestingCost.toLocaleString('en-NG')}` : '—'}
                    {' · '}Bagging: {form.expectedBaggingCost != null ? `₦${form.expectedBaggingCost.toLocaleString('en-NG')}` : '—'}
                    {' · '}Transportation: {form.expectedTransportationCost != null ? `₦${form.expectedTransportationCost.toLocaleString('en-NG')}` : '—'}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>
        )}

        {isLivestock && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Livestock Details</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth label="Animal Type"
                  placeholder="e.g. Catfish, Broiler Chicken, Goat"
                  value={form.animalType ?? ''}
                  onChange={(e) => set('animalType', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" label="Quantity (head count)"
                  value={form.animalQuantity ?? ''}
                  onChange={(e) => set('animalQuantity', Number.parseInt(e.target.value) || undefined)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth type="number" label="Expected Revenue (₦)"
                  value={form.expectedRevenue ?? ''}
                  onChange={(e) => set('expectedRevenue', Number.parseFloat(e.target.value) || undefined)}
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => navigate('/dashboard/farm')} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            type="submit" variant="contained" size="large"
            disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={18} /> : undefined}
          >
            {mutation.isPending ? 'Creating...' : 'Create Operation'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
